/**
 * NaamDarbar — App Root Component with Route Definitions
 *
 * All page components are lazy-loaded so each route gets its own JS chunk.
 * Only layout primitives (Layout, ScrollToTop, ProtectedRoute, redirect
 * helpers) are eagerly bundled — they ship on every navigation anyway.
 */

import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/ui/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import RedirectByArtistId from './components/RedirectByArtistId';
import RedirectByGurudwaraId from './components/RedirectByGurudwaraId';
import AdminLayout from './components/admin/AdminLayout';
import AuthLoadingScreen from './components/ui/AuthLoadingScreen';
import { useAuth } from './context/AuthContext';

// ── Lazy page imports ────────────────────────────────────────────────
const HomePage                   = lazy(() => import('./pages/HomePage'));
const RagiListPage               = lazy(() => import('./pages/RagiListPage'));
const ArtistProfilePage          = lazy(() => import('./pages/ArtistProfilePage'));
const EventListPage              = lazy(() => import('./pages/EventListPage'));
const EventDetailPage            = lazy(() => import('./pages/EventDetailPage'));
const SearchPage                 = lazy(() => import('./pages/SearchPage'));
const GurudwaraDetailPage         = lazy(() => import('./pages/GurudwaraDetailPage'));
const GurudwaraListPage           = lazy(() => import('./pages/GurudwaraListPage'));
const CityDetailPage             = lazy(() => import('./pages/CityDetailPage'));
const AmritSancharListPage       = lazy(() => import('./pages/AmritSancharListPage'));
const AmritSancharDetailPage     = lazy(() => import('./pages/AmritSancharDetailPage'));
const AmritSancharRegisterPage   = lazy(() => import('./pages/AmritSancharRegisterPage'));
const CampaignDetailPage         = lazy(() => import('./pages/CampaignDetailPage'));
const YatraListPage              = lazy(() => import('./pages/YatraListPage'));
const YatraDetailPage            = lazy(() => import('./pages/YatraDetailPage'));
const YatraRegisterPage          = lazy(() => import('./pages/YatraRegisterPage'));
const GurmatCampListPage         = lazy(() => import('./pages/GurmatCampListPage'));
const GurmatCampDetailPage       = lazy(() => import('./pages/GurmatCampDetailPage'));
const GurmatCampRegisterPage     = lazy(() => import('./pages/GurmatCampRegisterPage'));
const KirtanCityPage             = lazy(() => import('./pages/KirtanCityPage'));
const SamagamCityPage            = lazy(() => import('./pages/SamagamCityPage'));
const AKJSamagamPage             = lazy(() => import('./pages/AKJSamagamPage'));
const KirtanCityTodayPage        = lazy(() => import('./pages/KirtanCityTodayPage'));
const KirtanCityTypePage         = lazy(() => import('./pages/KirtanCityTypePage'));
const StateLandingPage           = lazy(() => import('./pages/StateLandingPage'));
const KirtanNearMePage           = lazy(() => import('./pages/KirtanNearMePage'));
const SamagamNearMePage          = lazy(() => import('./pages/SamagamNearMePage'));
const FAQPage                    = lazy(() => import('./pages/FAQPage'));
const KirtanByCityPage           = lazy(() => import('./pages/KirtanByCityPage'));
const GurudwaraSchedulePage       = lazy(() => import('./pages/GurudwaraSchedulePage'));
const ArtistEventsPage           = lazy(() => import('./pages/ArtistEventsPage'));
const HukamnamaPage              = lazy(() => import('./pages/HukamnamaPage'));
const HukamnamaArchivePage       = lazy(() => import('./pages/HukamnamaArchivePage'));
const GurupurabDetailPage         = lazy(() => import('./pages/GurupurabDetailPage'));
const RaagDetailPage             = lazy(() => import('./pages/RaagDetailPage'));
const JathaDetailPage            = lazy(() => import('./pages/JathaDetailPage'));
const BookRagiPage               = lazy(() => import('./pages/BookRagiPage'));
const BookArtistPage             = lazy(() => import('./pages/BookArtistPage'));
const SikhCalendarPage           = lazy(() => import('./pages/SikhCalendarPage'));
const CalendarSearchPage         = lazy(() => import('./pages/CalendarSearchPage'));
const PrivacyPolicyPage          = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage         = lazy(() => import('./pages/TermsOfServicePage'));
const LoginPage                  = lazy(() => import('./pages/LoginPage'));
const SignUpPage                 = lazy(() => import('./pages/SignUpPage'));
const SignUpWizardPage           = lazy(() => import('./pages/SignUpWizardPage'));
const ClaimStatusPage            = lazy(() => import('./pages/ClaimStatusPage'));
const ClaimProfilePage           = lazy(() => import('./pages/ClaimProfilePage'));
const ProfilePage                = lazy(() => import('./pages/ProfilePage'));
const RagiProfileEditPage        = lazy(() => import('./pages/RagiProfileEditPage'));
const RagiDashboardPage          = lazy(() => import('./pages/RagiDashboardPage'));
const NotFoundPage               = lazy(() => import('./pages/NotFoundPage'));
const SevadaarsPage              = lazy(() => import('./pages/SevadaarsPage'));

// Admin pages
const AdminLoginPage             = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboardPage         = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminArtistsPage           = lazy(() => import('./pages/admin/AdminArtistsPage'));
const AdminGurudwarasPage         = lazy(() => import('./pages/admin/AdminGurudwarasPage'));
const AdminEventsPage            = lazy(() => import('./pages/admin/AdminEventsPage'));
const AdminAmritSancharPage      = lazy(() => import('./pages/admin/AdminAmritSancharPage'));
const AdminCronjobsPage          = lazy(() => import('./pages/admin/AdminCronjobsPage'));
const AdminClaimsPage            = lazy(() => import('./pages/admin/AdminClaimsPage'));
const AdminClaimDetailPage       = lazy(() => import('./pages/admin/AdminClaimDetailPage'));
const AdminDuplicatesPage        = lazy(() => import('./pages/admin/AdminDuplicatesPage'));
const AdminGurudwaraDuplicatesPage = lazy(() => import('./pages/admin/AdminGurudwaraDuplicatesPage'));
const AdminEventDuplicatesPage   = lazy(() => import('./pages/admin/AdminEventDuplicatesPage'));
const AdminRagiMergePage         = lazy(() => import('./pages/admin/AdminRagiMergePage'));
const AdminIngestPage            = lazy(() => import('./pages/admin/AdminIngestPage'));
const AdminBirdhSewaPage         = lazy(() => import('./pages/admin/AdminBirdhSewaPage'));
const AdminUsersPage             = lazy(() => import('./pages/admin/AdminUsersPage'));
const OnboardingPage             = lazy(() => import('./pages/OnboardingPage'));
const BirdhSewaPage              = lazy(() => import('./pages/BirdhSewaPage'));

// ── Page-level loading fallback ──────────────────────────────────────
function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Ping the backend every 4 minutes to prevent Railway container from sleeping
const KEEP_ALIVE_INTERVAL_MS = 4 * 60 * 1000;
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Paths exempt from ALL onboarding/claim gates
const GATE_EXEMPT = [
  '/onboarding', '/login', '/signup', '/admin', '/logout',
  '/privacy-policy', '/terms-of-service',
  '/claim/status',  // rejected users must be able to see their status + restart
];

/**
 * Onboarding gate — role selection only (Tier 1).
 *
 * 1. Role not chosen → /onboarding
 *
 * The ragi profile-claim is now OPTIONAL. Once a role is picked, the user
 * lands in the app like anyone else. Raagi-intent users with an unfinished
 * claim are nudged via a dismissable banner + the dedicated /claim page —
 * never force-redirected. Resume logic lives in `lib/claim.ts` and is driven
 * by explicit "Continue" CTAs, not this gate.
 */
function OnboardingGate() {
  const { isAuthenticated, isLoadingAuth, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Wait only when there is genuinely no profile data yet.
    // For returning users, `profile` is pre-populated from sessionStorage cache
    // so the gate can fire immediately — no need to wait for the /auth/me round-trip.
    if (isLoadingAuth && profile === null) return;
    if (!isAuthenticated) return;
    if (profile === null) return;
    if (GATE_EXEMPT.some((p) => location.pathname.startsWith(p))) return;

    // Tier 1: no role chosen → pick one (single tap, then optional from here on).
    if (profile.intended_role === null || profile.intended_role === undefined) {
      navigate('/onboarding', { replace: true });
    }
  }, [isLoadingAuth, isAuthenticated, profile, location.pathname, navigate]);

  return null;
}

/**
 * Auth loading gate — isolated so only this small component (not App and the
 * whole route tree) subscribes to auth context. Any profile refresh flips
 * `profileLoading` twice; subscribing in App re-rendered every mounted page.
 *
 * Only shows the full-screen loading screen when there is no cached profile to
 * render with. Returning users have profile in sessionStorage so the app
 * renders immediately; Firebase session re-validation happens in the background.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const { isLoadingAuth, profile } = useAuth();
  if (isLoadingAuth && !profile) return <AuthLoadingScreen />;
  return children;
}

export default function App() {
  useEffect(() => {
    const ping = () =>
      fetch(`${API_BASE_URL}/health`).catch(() => {/* silent — keep-alive only */});
    ping(); // immediate ping on mount to warm up the connection
    const id = setInterval(ping, KEEP_ALIVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <AuthGate>
      <ScrollToTop />
      {/* Renders nothing — fires redirect side-effect for unboarded users */}
      <OnboardingGate />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* ── Public routes ──────────────────────────────────────────── */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/ragis" element={<RagiListPage />} />
            {/* /ragis/:id → redirect to canonical /artist/:slug (kills duplicate PageRank split) */}
            <Route path="/ragis/:id" element={<RedirectByArtistId />} />
            <Route path="/artist/:slug" element={<ArtistProfilePage />} />
            <Route path="/events" element={<EventListPage />} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/gurudwaras" element={<GurudwaraListPage />} />
            {/* /gurudwara/by-id/:id → redirect to canonical /gurudwara/:slug */}
            <Route path="/gurudwara/by-id/:id" element={<RedirectByGurudwaraId />} />
            <Route path="/gurudwara/:slug" element={<GurudwaraDetailPage />} />
            <Route path="/city/:slug" element={<CityDetailPage />} />
            {/* ── Programmatic SEO routes (order matters — specific before generic) */}
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/birdh-sewa" element={<BirdhSewaPage />} />
            <Route path="/kirtan-by-city" element={<KirtanByCityPage />} />
            <Route path="/kirtan-near-me" element={<KirtanNearMePage />} />
            <Route path="/samagam-near-me" element={<SamagamNearMePage />} />
            <Route path="/akj-samagam" element={<AKJSamagamPage />} />
            <Route path="/kirtan/:citySlug/today" element={<KirtanCityTodayPage />} />
            <Route path="/kirtan/:stateSlug/state" element={<StateLandingPage />} />
            <Route path="/kirtan/:citySlug/:eventType" element={<KirtanCityTypePage />} />
            <Route path="/kirtan/:citySlug" element={<KirtanCityPage />} />
            <Route path="/samagam/:citySlug" element={<SamagamCityPage />} />
            <Route path="/gurudwara/:slug/kirtan-schedule" element={<GurudwaraSchedulePage />} />
            <Route path="/artist/:slug/upcoming-events" element={<ArtistEventsPage />} />
            <Route path="/hukamnama" element={<HukamnamaPage />} />
            <Route path="/hukamnama/archive" element={<HukamnamaArchivePage />} />
            <Route path="/gurupurab/:slug" element={<GurupurabDetailPage />} />
            <Route path="/raag/:slug" element={<RaagDetailPage />} />
            <Route path="/jatha/:slug" element={<JathaDetailPage />} />
            <Route path="/book-ragi" element={<BookRagiPage />} />
            <Route path="/book/:slug" element={<BookArtistPage />} />
            <Route path="/calendar" element={<SikhCalendarPage />} />
            <Route path="/calendar/search" element={<CalendarSearchPage />} />
            <Route path="/amrit-sanchar" element={<AmritSancharListPage />} />
            <Route path="/amrit-sanchar/campaign/:id" element={<CampaignDetailPage />} />
            <Route path="/amrit-sanchar/:id" element={<AmritSancharDetailPage />} />
            <Route path="/amrit-sanchar/:id/register" element={<AmritSancharRegisterPage />} />
            <Route path="/yatra" element={<YatraListPage />} />
            <Route path="/yatra/:id" element={<YatraDetailPage />} />
            <Route path="/yatra/:id/register" element={<YatraRegisterPage />} />
            <Route path="/gurmat-camps" element={<GurmatCampListPage />} />
            <Route path="/gurmat-camps/:id" element={<GurmatCampDetailPage />} />
            <Route path="/gurmat-camps/:id/register" element={<GurmatCampRegisterPage />} />

            <Route path="/sevadaars" element={<SevadaarsPage />} />

            {/* ── Legal pages ─────────────────────────────────────────── */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* ── Auth routes ─────────────────────────────────────────── */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Legacy simple signup — kept for any deep-links that still point here */}
            <Route path="/signup-simple" element={<SignUpPage />} />
            {/* New wizard signup (Sangat 2-step / Raagi 6-step) */}
            <Route path="/signup" element={<SignUpWizardPage />} />
            <Route path="/signup/:step" element={<SignUpWizardPage />} />
            {/* Legacy claim-profile deep-link redirect */}
            <Route path="/claim-profile" element={<SignUpWizardPage />} />
            {/* Dedicated claim hub — resume / start / view status anytime */}
            <Route
              path="/claim"
              element={
                <ProtectedRoute>
                  <ClaimProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/claim/status"
              element={
                <ProtectedRoute>
                  <ClaimStatusPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireRole="raagi">
                  <RagiDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            {/* Legacy ragi-profile editor → now the Dashboard "Profile" tab. */}
            <Route
              path="/profile/ragi"
              element={
                <ProtectedRoute>
                  <RagiProfileEditPage />
                </ProtectedRoute>
              }
            />

            {/* ── 404 catch-all ───────────────────────────────────────── */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* ── Admin routes ───────────────────────────────────────────── */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/artists" element={<AdminArtistsPage />} />
            <Route path="/admin/gurudwaras" element={<AdminGurudwarasPage />} />
            <Route path="/admin/events" element={<AdminEventsPage />} />
            <Route path="/admin/ingest" element={<AdminIngestPage />} />
            <Route path="/admin/amrit-sanchar" element={<AdminAmritSancharPage />} />
            <Route path="/admin/cronjobs" element={<AdminCronjobsPage />} />
            <Route path="/admin/claims" element={<AdminClaimsPage />} />
            <Route path="/admin/claims/:claimId" element={<AdminClaimDetailPage />} />
            <Route path="/admin/duplicates" element={<AdminDuplicatesPage />} />
            <Route path="/admin/gurudwara-duplicates" element={<AdminGurudwaraDuplicatesPage />} />
            <Route path="/admin/event-duplicates" element={<AdminEventDuplicatesPage />} />
            <Route path="/admin/ragi-merge" element={<AdminRagiMergePage />} />
            <Route path="/admin/birdh-sewa-leads" element={<AdminBirdhSewaPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthGate>
  );
}
